import { Share2, Play, Linkedin } from "lucide-react"

import { usePmbSection } from "~/hooks/pre-meeting"
import { type PmbSectionCtx } from "~/api/pre-meeting-v2"
import { type SocialMediaPayload } from "~/types/pre-meeting"

import { SectionShell } from "../section-ui"

// Presentational view: shared by the drawer and the ASKOS agent widget.
export function SocialMediaSectionView({
  payload,
  isLoading = false,
  error = null
}: {
  payload?: SocialMediaPayload | null
  isLoading?: boolean
  error?: string | null
}) {
  const linkedin = payload?.linkedin || []
  const youtube = payload?.youtube || []
  const isEmpty = !linkedin.length && !youtube.length

  return (
    <SectionShell
      id="social_media"
      styleKey="social_media"
      icon={<Share2 size={16} />}
      title="Social Media"
      subtitle="Recent activity from this person"
      isLoading={isLoading}
      error={error}
      isEmpty={isEmpty}
      emptyText="No recent social activity found."
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {youtube.map((v) => (
          <a
            key={v.link}
            href={v.link}
            target="_blank"
            rel="noreferrer"
            className="rounded-lg overflow-hidden border border-light-border/40 dark:border-border/40 hover:shadow-sm transition-all"
          >
            {v.thumbnail ? (
              <img
                src={v.thumbnail}
                alt={v.title}
                className="w-full h-32 object-cover"
              />
            ) : (
              <div className="w-full h-32 flex items-center justify-center bg-[#7c3aed]/10">
                <Play size={20} className="text-[#7c3aed]" />
              </div>
            )}
            <div className="p-2">
              <p className="text-[12px] font-medium text-dark-2 dark:text-dark-6 line-clamp-2">
                {v.title}
              </p>
              <p className="text-[10px] text-gray-400 mt-0.5">
                {[v.channel, v.published].filter(Boolean).join(" . ")}
              </p>
            </div>
          </a>
        ))}

        {linkedin.map((post) => (
          <a
            key={post.link}
            href={post.link}
            target="_blank"
            rel="noreferrer"
            className="p-3 rounded-lg border border-light-border/40 dark:border-border/40 hover:shadow-sm transition-all flex flex-col gap-1"
          >
            <span className="flex items-center gap-1 text-[10px] text-[#0a66c2]">
              <Linkedin size={12} /> LinkedIn
            </span>
            <p className="text-[12px] font-medium text-dark-2 dark:text-dark-6 line-clamp-3">
              {post.title}
            </p>
            {post.date && (
              <p className="text-[10px] text-gray-400">
                {new Date(post.date).toLocaleDateString()}
              </p>
            )}
          </a>
        ))}
      </div>
    </SectionShell>
  )
}

export function SocialMediaSection({
  ctx,
  enabled
}: {
  ctx: PmbSectionCtx
  enabled: boolean
}) {
  const { data, isLoading, isError } = usePmbSection<SocialMediaPayload>(
    "social_media",
    ctx,
    enabled
  )
  const error =
    data?.error || (isError ? "Could not load social activity." : null)
  return (
    <SocialMediaSectionView
      payload={data?.payload}
      isLoading={isLoading}
      error={error}
    />
  )
}
